import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { Loading, ErrorState, fmtDate } from '../../components/ui';
import { Panel } from '../../components/charts';
import { money } from './Dashboard';

/* ============================================================
   Satu sertifikat pembayaran.

   Baris yang tertahan tetap terlihat, dengan sebabnya. Baris yang
   hilang tanpa penjelasan berubah menjadi telepon marah dalam dua
   jam, dan menjadi kebiasaan mengabaikan gerbang dalam dua minggu.
   ============================================================ */

type Line = {
  qtyToDate: string | number;
  rate: string | number;
  valueToDate: string | number;
  held: boolean;
  holdReason: string | null;
  holdNote: string | null;
  boqItem: { itemNo: string; description: string; unit: string; contractQty: string | number };
};

type Detail = {
  certificate: {
    id: string;
    certNo: number;
    state: string;
    grossToDate: string | number;
    variationsToDate: string | number;
    retentionToDate: string | number;
    advanceRecoveredToDate: string | number;
    certifiedToDate: string | number;
    previouslyCertified: string | number;
    netThisCertificate: string | number;
    heldValue: string | number;
    issuedAt: string | null;
    contract: { id: string; contractNo: string; title: string; currency: string };
    period: { periodNo: number; start: string; end: string; status: string };
  };
  lines: Line[];
};

const n = (v: string | number) => Number(v);

const HOLD_LABEL: Record<string, string> = {
  UNVERIFIED: 'Belum diverifikasi',
  DELIVERABLE_NOT_IFC: 'Gambar belum IFC',
  NCR_OPEN: 'NCR terbuka',
  MANUAL: 'Ditahan manual',
};

export function Certificate() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api.get<Detail>(`/cost/certificates/${id}`));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the certificate.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function issue() {
    setBusy(true);
    try {
      await api.post(`/cost/certificates/${id}/issue`);
      setNotice('Sertifikat terbit. Ia tidak bisa disusun ulang — koreksi terbawa di periode berikutnya.');
      await load();
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : 'Gagal menerbitkan.');
    } finally {
      setBusy(false);
    }
  }

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading rows={5} />;

  const c = data.certificate;
  const held = data.lines.filter((l) => l.held);

  const rows: [string, number, boolean?][] = [
    ['Nilai pekerjaan terukur', n(c.grossToDate)],
    ['Variation order disetujui', n(c.variationsToDate)],
    ['Dikurangi retensi', -n(c.retentionToDate)],
    ['Dikurangi pemulihan uang muka', -n(c.advanceRecoveredToDate)],
    ['Tersertifikasi sampai periode ini', n(c.certifiedToDate), true],
    ['Dikurangi yang sudah disertifikasi', -n(c.previouslyCertified)],
    ['Neto periode ini', n(c.netThisCertificate), true],
  ];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>IPC {c.certNo}</h1>
          <p>
            {c.contract.contractNo} · periode {c.period.periodNo} ·{' '}
            {fmtDate(c.period.start)} – {fmtDate(c.period.end)}
          </p>
        </div>
        <div className="filters" style={{ marginBottom: 0 }}>
          <span className={`chip ${c.state === 'DRAFT' ? '' : 'good'}`}>{c.state}</span>
          {c.state === 'DRAFT' && (
            <button className="btn btn-primary btn-sm" disabled={busy} onClick={issue}>
              Terbitkan
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={() => nav(`/contracts/${c.contract.id}`)}>
            Kembali
          </button>
        </div>
      </div>

      {notice && <div className="banner">{notice}</div>}

      <div className="grid-2">
        <Panel title="Perhitungan" note="Kumulatif sampai periode ini, dikurangi yang sudah dibayar.">
          <div className="tbl-wrap">
            <table className="tbl">
              <tbody>
                {rows.map(([label, value, strong]) => (
                  <tr key={label} className="no-click">
                    <td style={strong ? { fontWeight: 600 } : undefined}>{label}</td>
                    <td
                      className={value < 0 ? 'neg' : undefined}
                      style={{ textAlign: 'right', fontWeight: strong ? 700 : undefined }}
                    >
                      {money(value, c.contract.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {n(c.netThisCertificate) < 0 && (
            <p className="panel-foot">
              Neto negatif. Ini terjadi ketika verifikasi ulang menurunkan nilai yang sudah terlanjur
              dibayar, dan angkanya sengaja tidak dibatasi di nol — kalau dibatasi, kelebihan bayar
              itu tidak akan pernah pulih.
            </p>
          )}
        </Panel>

        <Panel
          title="Tertahan"
          note={
            held.length === 0
              ? 'Tidak ada baris yang tertahan pada sertifikat ini.'
              : 'Nilai yang tidak disertifikasi, beserta sebabnya.'
          }
        >
          {held.length === 0 ? (
            <p className="hint">Semua baris lolos gerbang.</p>
          ) : (
            <>
              <p className="kpi-value" style={{ marginBottom: 12 }}>
                {money(n(c.heldValue), c.contract.currency)}
              </p>
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Nilai</th>
                      <th>Sebab</th>
                    </tr>
                  </thead>
                  <tbody>
                    {held.map((l) => (
                      <tr key={l.boqItem.itemNo} className="no-click">
                        <td className="num">{l.boqItem.itemNo}</td>
                        <td>{money(n(l.valueToDate), c.contract.currency)}</td>
                        <td>
                          <span className="chip warn">
                            {HOLD_LABEL[l.holdReason ?? ''] ?? l.holdReason}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Panel>
      </div>

      <Panel title="Baris" note="Kuantitas kumulatif menurut verifikasi lapangan." wide>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Item</th>
                <th>Uraian</th>
                <th>Satuan</th>
                <th>Kontrak</th>
                <th>Terukur</th>
                <th>Nilai</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.lines.map((l) => (
                <tr key={l.boqItem.itemNo} className="no-click">
                  <td className="num">{l.boqItem.itemNo}</td>
                  <td>{l.boqItem.description}</td>
                  <td>{l.boqItem.unit}</td>
                  <td>{n(l.boqItem.contractQty).toLocaleString('id-ID')}</td>
                  <td>{n(l.qtyToDate).toLocaleString('id-ID')}</td>
                  <td>{money(n(l.valueToDate), c.contract.currency)}</td>
                  <td>
                    {l.held ? (
                      <span className="chip warn" title={l.holdNote ?? undefined}>
                        {HOLD_LABEL[l.holdReason ?? ''] ?? 'Tertahan'}
                      </span>
                    ) : (
                      <span className="chip good">Disertifikasi</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
}
